/**
 * web/src/screens/Notebook/hooks/derive.ts — the pure derived view of
 * useNotebookState (card vncVrGk1hc9i, round two): everything the feed
 * renders computes from the seams' state in one pure function here. Pure
 * move out of the former hooks.ts — no behaviour change.
 */
import { NOTEBOOK_LINK_KINDS, NOTEBOOK_LINK_SUBTYPES } from '../../../../../shared/types';
import { deriveClusters, type ClusterPrefs, type EntryType, type NotebookItem, type NotebookTag } from '../types';

export function deriveListView({ items, collection, kindFilter, subtypeFilter, statusFilter, tags, clusterPrefs, contextLive, feedRoving }: {
  items: NotebookItem[] | null;
  collection: 'all' | EntryType;
  kindFilter: string | 'all';
  subtypeFilter: string | 'all';
  statusFilter: 'all' | 'saved' | 'planned' | 'done';
  tags: NotebookTag[];
  clusterPrefs: ClusterPrefs;
  contextLive: boolean;
  feedRoving: string | null;
}) {
  const linkItems = (items ?? []).filter(i => i.entryType === 'link');

  /* Card xUZFWf8JtHo2 item 2 — the category counts (the dropdown's option
     labels, formerly the chip index) count the links the current
     search / status / subtype leave on the table, so the numbers agree with the
     cards actually shown (before this they read the raw link total, so a
     narrowing left stale counts). The KIND filter itself is excluded on
     purpose: choosing one kind must never hide the other categories behind a
     count of 0. */
  const chipLinks = (items ?? []).filter(i => {
    if (i.entryType !== 'link') return false;
    if (subtypeFilter !== 'all' && (i.subtype ?? '') !== subtypeFilter) return false;
    if (statusFilter !== 'all' && (i.status ?? 'saved') !== statusFilter) return false;
    // No text filter here: the search is the server's query (card
    // UVRx4xesTEig), so `items` already holds only what matched.
    return true;
  });

  /** Every kind that has at least one card, in taxonomy order — the rows of
   *  the category dropdown. The row STAYS as long as the kind exists in the
   *  full link set (the dropdown is the only way to switch kinds now, so a
   *  status/subtype/search filter must never make it vanish) — but its COUNT
   *  reflects the narrowed set, so a kind with nothing matching reads an
   *  honest 0. */
  const kindChips = NOTEBOOK_LINK_KINDS
    .map(kind => ({
      kind,
      n: chipLinks.filter(i => i.kind === kind).length,
      total: linkItems.filter(i => i.kind === kind).length,
    }))
    .filter(c => c.total > 0);

  /** Subtypes actually present under the chosen kind (open-ended: anything the
   *  classifier or the user wrote is shown, named kinds first). */
  const subtypeTabs = kindFilter === 'all'
    ? []
    : [...new Set([
        ...(NOTEBOOK_LINK_SUBTYPES[kindFilter as keyof typeof NOTEBOOK_LINK_SUBTYPES] ?? []),
        ...linkItems.filter(i => i.kind === kindFilter && i.subtype).map(i => i.subtype as string),
      ])].filter(s => linkItems.some(i => i.kind === kindFilter && i.subtype === s));

  const visible = (items ?? []).filter(i => {
    if (collection !== 'all' && i.entryType !== collection) return false;
    /* The links view's narrowing applies to LINKS only (sol review 2026-10-17):
       a typed entry has no kind, subtype or status to answer to, and its filter
       controls are hidden on its own tab — so a category choice or a search left
       over from the Links view must never empty Memories, Notes or Ideas. The exemption is
       on the ENTRY TYPE, not on the collection, because switching tabs keeps
       these controls' state.

       Card xUZFWf8JtHo2 item 3 + card UVRx4xesTEig — the search itself is no
       longer narrowed here: it is the server's query (GET /api/notebook?q=…),
       so `items` is already the matched page and a leftover query can never
       empty Memories, Notes or Ideas — the server matched their body/title/
       place (and tags) exactly as the old client haystack did. */
    if (i.entryType !== 'link') return true;
    if (kindFilter !== 'all' && i.kind !== kindFilter) return false;
    if (subtypeFilter !== 'all' && (i.subtype ?? '') !== subtypeFilter) return false;
    if (statusFilter !== 'all' && (i.status ?? 'saved') !== statusFilter) return false;
    return true;
  });

  /* PRD E3/E4 — the groups themselves. Rendered only where the panel a member
     row opens actually exists (>=760px) and only on the Everything tab: below
     that hinge the phone keeps the plain list it has always had (PRD L5), and
     a group under a type tab would just repeat what the tab already says. */
  const clusters = collection === 'all' && contextLive
    ? deriveClusters(visible, tags, clusterPrefs)
    : [];
  const clusterPrefsDirty = clusterPrefs.dissolved.length > 0
    || clusterPrefs.excluded.length > 0
    || Object.values(clusterPrefs.added).some(v => v.length > 0);

  /* PRD §8 — exactly ONE card is a tab stop at a time (roving tabindex), so
     Tab enters the feed once instead of stopping at every card while the
     Arrow keys still walk the whole list. The stop falls back to the first
     visible card when the roving card was filtered away or never focused. */
  const rovingCardId =
    feedRoving && visible.some(v => v.itemId === feedRoving)
      ? feedRoving
      : (visible[0]?.itemId ?? null);

  /* Card xUZFWf8JtHo2 item 1 — the counter must answer "how much of what this
     tab holds is on screen?", so its base follows the tab: every card on
     Everything, links on the Link tab. It used to count link cards against the
     link total on BOTH tabs, so Everything read "1 of 1" above three cards. The
     numerator is always the rendered count. */
  const counterBase = collection === 'link' ? linkItems.length : (items ?? []).length;

  return {
    linkItems, chipLinks, kindChips, subtypeTabs, visible,
    clusters, clusterPrefsDirty, rovingCardId, counterBase,
  };
}
